import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import CourseCard from "../../components/Course/CourseCard";
import CourseService from "../../service/courseService";
import "./CourseOneMain.css";
import courseBG from "../../assets/img/course/ed-bg-1.jpg";

const CourseOneMain = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const levelId = searchParams.get("levelId");
  const requestedPage = Number(searchParams.get("page") || 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const [perPage, setPerPage] = useState(() => window.matchMedia("(max-width: 767px)").matches ? 6 : 12);
  const [pagination, setPagination] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onChange = () => {
      setPerPage(media.matches ? 6 : 12);
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete("page");
        return next;
      }, { replace: true });
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    CourseService.getCourses({ signal: controller.signal, page, perPage, academicLevelId: levelId })
      .then((data) => {
        if (controller.signal.aborted) return;

        console.log("[Cours par niveau] Academic level and stage:", {
          levelId,
          courses: data.data.map((course) => ({
            courseId: course.id,
            academicLevel: course.academic_level,
            academicStage: course.academic_level?.academic_stage,
          })),
        });
        if (page > data.last_page) {
          setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            next.set("page", String(data.last_page));
            return next;
          }, { replace: true });
          return;
        }
        setCourses(data.data);
        setPagination(data);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setError(!error.response && error.code === "ERR_NETWORK"
            ? "Impossible de joindre le serveur. Veuillez réessayer."
            : error.response?.status === 401 || error.response?.status === 403
              ? "L’accès aux cours n’est pas encore public."
              : "Impossible de charger les cours. Veuillez réessayer.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [attempt, levelId, page, perPage, setSearchParams]);

  const goToPage = (nextPage) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("page", String(nextPage));
      return next;
    });
    document.getElementById("it-course")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const pageNumbers = pagination ? Array.from(new Set([
    1, page - 1, page, page + 1, pagination.last_page,
  ].filter((value) => value >= 1 && value <= pagination.last_page))).sort((a, b) => a - b) : [];

  return (
    <main>
      <Breadcrumb title={levelId ? "Cours par niveau" : "Tous les cours"} subTitle="Cours" />
      <section
        id="it-course"
        className="it-course-area ed-course-bg ed-course-style-3 p-relative pt-120 pb-90"
        style={{ backgroundImage: `url(${courseBG})` }}
        aria-label="Liste des cours"
        aria-busy={loading}
      >
        <div className="container">
          <h2 className="mb-65">Découvrez nos cours</h2>
          {loading ? (
            <p role="status">Chargement des cours…</p>
          ) : error ? (
            <div>
              <p role="alert">{error}</p>
              <button className="ed-btn-theme" type="button" onClick={() => setAttempt((value) => value + 1)}>
                Réessayer
              </button>
            </div>
          ) : courses.length === 0 ? (
            <p role="status">Aucun cours disponible pour le moment.</p>
          ) : (
            <div className="row">
              {courses.map((course) => (
                <div key={course.id} className="col-xl-4 col-lg-6 col-md-6 mb-30">
                  <CourseCard course={course} />
                </div>
              ))}
            </div>
          )}
          {!loading && !error && pagination?.total > 0 && (
            <div className="course-pagination">
              <p className="course-pagination-summary" role="status">
                {pagination.from}–{pagination.to} sur {pagination.total} cours
              </p>
              {pagination.last_page > 1 && (
                <nav aria-label="Pagination des cours" className="course-pagination-controls">
                  <button type="button" disabled={page === 1} onClick={() => goToPage(page - 1)} aria-label="Page précédente">‹</button>
                  {pageNumbers.map((number, index) => (
                    <React.Fragment key={number}>
                      {index > 0 && number - pageNumbers[index - 1] > 1 && <span className="course-pagination-gap" aria-hidden="true">…</span>}
                      <button type="button" aria-label={`Page ${number}`} aria-current={page === number ? "page" : undefined} onClick={() => goToPage(number)}>{number}</button>
                    </React.Fragment>
                  ))}
                  <button type="button" disabled={page === pagination.last_page} onClick={() => goToPage(page + 1)} aria-label="Page suivante">›</button>
                </nav>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default CourseOneMain;
